/**
 * The value RAMP of the design's bar charts (D549, DS-CHARTS). A bar is coloured by how large it is next to the largest bar:
 * low values are pale, the peak is ink. The Claude Design draws two ramps by hand; these thresholds are the rule they follow,
 * checked against the exact heights of every design chart in `ramp.test.ts` (Admin A02 hourly rides, the report-builder
 * preview, Merchant MRC-014). Pure: the step is an index, the colour belongs to `@vello/tokens` (`--chart-ramp-*`).
 */
import { clamp, isFiniteNumber } from './numbers.js';
/** Upper bounds (as a fraction of the maximum) of ramp steps 1…4 of the five-step neutral ramp (Admin A02): steps are ≤34 %, ≤59 %, ≤75 %, ≤88 %, above. */
export const RAMP_THRESHOLDS_NEUTRAL = [0.34, 0.59, 0.75, 0.88];
/** Upper bounds of ramp steps 1…2 of the three-step tint ramp (report builder, Merchant): ≤70 %, ≤90 %, above. */
export const RAMP_THRESHOLDS_TINT = [0.7, 0.9];
/**
 * The zero-based ramp step of `value` against `max`: the number of thresholds the fraction exceeds. A value that is not a
 * finite number, or a non-positive maximum, is the lowest step; the result never exceeds `thresholds.length`.
 */
export function rampIndex(value, max, thresholds = RAMP_THRESHOLDS_NEUTRAL) {
    if (!isFiniteNumber(value) || !isFiniteNumber(max) || max <= 0)
        return 0;
    const f = clamp(value / max, 0, 1);
    let step = 0;
    for (const t of thresholds)
        if (f > t)
            step += 1;
    return step;
}
/** The step one notch stronger (a hovered bar), clamped to the top of the ramp of `stepCount` steps. */
export function rampStepUp(index, stepCount) {
    return Math.min(Math.max(0, Math.floor(index)) + 1, Math.max(0, Math.floor(stepCount) - 1));
}
/**
 * A band scale whose gaps are an exact pixel width (the design's bars sit 6px apart whatever their count), with the first bar
 * flush to the start and the last to the end. Same shape as `bandScale`, so a chart can swap one for the other.
 */
export function gappedBandScale(count, range, gapPx) {
    const n = Math.max(0, Math.floor(isFiniteNumber(count) ? count : 0));
    const [r0, r1] = range;
    const gap = Math.max(0, isFiniteNumber(gapPx) ? gapPx : 0);
    const width = Math.max(0, r1 - r0);
    const bandwidth = n === 0 ? 0 : Math.max(1, (width - gap * (n - 1)) / n);
    const step = bandwidth + gap;
    const start = (index) => r0 + index * step;
    const indexAt = (position) => {
        if (n === 0 || !isFiniteNumber(position))
            return -1;
        if (position < Math.min(r0, r1) || position > Math.max(r0, r1))
            return -1;
        return clamp(Math.floor((position - r0 + gap / 2) / step), 0, n - 1);
    };
    return { count: n, bandwidth, step, start, center: (index) => start(index) + bandwidth / 2, indexAt };
}
//# sourceMappingURL=ramp.js.map